import {
  createWalkOnSongInputSchema,
  teamProfileSchema,
  type Team,
  type TeamProfile,
} from '@/core/schemas';

export interface TeamFormValues {
  fireDepartment: string;
  name: string;
  songTitle: string;
  songArtist: string;
  songUrl: string;
  startSeconds: string;
  endSeconds: string;
}

export const emptyTeamForm: TeamFormValues = {
  fireDepartment: '',
  name: '',
  songTitle: '',
  songArtist: '',
  songUrl: '',
  startSeconds: '',
  endSeconds: '',
};

export function teamToFormValues(team: Team): TeamFormValues {
  return {
    fireDepartment: team.fireDepartment,
    name: team.name,
    songTitle: team.walkOnSong.title,
    songArtist: team.walkOnSong.artist ?? '',
    songUrl: team.walkOnSong.sourceUrl ?? '',
    startSeconds: String(team.walkOnSong.startSeconds),
    endSeconds: String(team.walkOnSong.endSeconds),
  };
}

/** `''` must not become `0`; an empty field is missing, not zero. */
function toNumber(value: string): number | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : Number(trimmed);
}

function toOptionalText(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

export type FieldErrors = Partial<Record<keyof TeamFormValues, string>>;

export type ValidationResult =
  | { readonly ok: true; readonly profile: TeamProfile }
  | { readonly ok: false; readonly errors: FieldErrors };

/** Maps schema paths onto form fields so each message lands at its input. */
const PATH_TO_FIELD: Readonly<Record<string, keyof TeamFormValues>> = {
  fireDepartment: 'fireDepartment',
  name: 'name',
  'walkOnSong.title': 'songTitle',
  'walkOnSong.artist': 'songArtist',
  'walkOnSong.sourceUrl': 'songUrl',
  'walkOnSong.startSeconds': 'startSeconds',
  'walkOnSong.endSeconds': 'endSeconds',
};

/**
 * Validates the form against the domain schema, extended by the maximum snippet
 * length taken from the tournament settings. The limit is never a literal in
 * this file — it arrives as an argument.
 */
export function validateTeamForm(
  values: TeamFormValues,
  songMaxDurationSeconds: number,
): ValidationResult {
  const schema = teamProfileSchema.extend({
    walkOnSong: createWalkOnSongInputSchema(songMaxDurationSeconds),
  });

  const candidate = {
    fireDepartment: values.fireDepartment,
    name: values.name,
    walkOnSong: {
      title: values.songTitle,
      artist: toOptionalText(values.songArtist),
      sourceUrl: toOptionalText(values.songUrl),
      startSeconds: toNumber(values.startSeconds),
      endSeconds: toNumber(values.endSeconds),
    },
  };

  const result = schema.safeParse(candidate);

  if (result.success) {
    return { ok: true, profile: result.data };
  }

  const errors: FieldErrors = {};

  for (const issue of result.error.issues) {
    const path = issue.path.join('.');
    const field = PATH_TO_FIELD[path];
    if (field !== undefined && errors[field] === undefined) {
      errors[field] = issue.message;
    }
  }

  return { ok: false, errors };
}

/** Snippet length for the live hint, or `null` while the input is incomplete. */
export function snippetDuration(values: TeamFormValues): number | null {
  const start = toNumber(values.startSeconds);
  const end = toNumber(values.endSeconds);

  if (start === undefined || end === undefined || Number.isNaN(start) || Number.isNaN(end)) {
    return null;
  }

  return end - start;
}
